import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendDown, TrendUp } from '@phosphor-icons/react';

import type { CollectionStatus } from '@/api/notices';

// The §11 "money story" dashboard for one team's notice run. Four figures tell
// one balanced story — expected − lost − outstanding = collected — rendered as a
// collection gauge, a revenue waterfall, a YoY delta and an honest forecast
// range. Dependency-free SVG, token-driven (lime = the single accent, ink for
// everything else, no pastels as foreground) per the design system.

interface Props {
  cs?: CollectionStatus | undefined;
  loading?: boolean | undefined;
  /** Money formatter (minor units → locale currency string). */
  formatMoney: (minor: number) => string;
}

// Segment colours: lime is the win (collected); in-flight and lost are neutral
// inks at different weights so the bar reads without reaching for a second hue
// or a "debt red" (a drop-out is a clean roster outcome, not a debt — §11.1).
const COLLECTED = 'var(--lime, #BEE034)';
const OUTSTANDING = 'color-mix(in srgb, currentColor 34%, transparent)';
const LOST = 'color-mix(in srgb, currentColor 14%, transparent)';
const TRACK = 'color-mix(in srgb, currentColor 8%, transparent)';

export function CollectionDashboard({ cs, loading, formatMoney }: Props) {
  const { t } = useTranslation('common');

  const pct = useMemo(() => {
    if (!cs || cs.expected_minor <= 0) return 0;
    return Math.min(100, Math.round((cs.collected_minor / cs.expected_minor) * 100));
  }, [cs]);

  if (loading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <div className="h-6 w-40 animate-pulse rounded-pill bg-muted" />
        <div className="h-4 w-full animate-pulse rounded-pill bg-muted" />
        <div className="h-24 w-full animate-pulse rounded-lg bg-muted" />
      </div>
    );
  }

  if (!cs || cs.expected_minor <= 0) {
    return <p className="text-sm text-foreground/60">{t('notices.collection.empty')}</p>;
  }

  const remaining = Math.max(0, cs.expected_minor - cs.collected_minor);
  const total = cs.expected_minor;
  // Waterfall segment widths (percent of the ceiling).
  const w = (v: number) => (total > 0 ? (Math.max(0, v) / total) * 100 : 0);
  const segments = [
    { key: 'collected', value: cs.collected_minor, fill: COLLECTED },
    { key: 'outstanding', value: cs.outstanding_minor, fill: OUTSTANDING },
    { key: 'lost', value: cs.lost_minor, fill: LOST },
  ].filter((s) => s.value > 0);

  return (
    <div className="flex flex-col gap-5">
      {/* Hero: collection gauge — the single most-glanced number. */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-wider text-foreground/60">
              {t('notices.collection.collected')}
            </p>
            <p className="font-heading text-3xl font-extrabold tracking-tight tabular-nums">
              {formatMoney(cs.collected_minor)}
              <span className="ml-2 text-base font-semibold text-foreground/60">{pct}%</span>
            </p>
          </div>
          <YoyBadge yoy={cs.yoy_percent} />
        </div>
        <div
          className="h-3 w-full overflow-hidden rounded-pill"
          style={{ background: TRACK }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-pill transition-all"
            style={{ width: `${pct}%`, background: COLLECTED }}
          />
        </div>
        <p className="text-sm text-foreground/70">
          {t('notices.collection.gaugeCaption', {
            remaining: formatMoney(remaining),
            expected: formatMoney(cs.expected_minor),
          })}
        </p>
      </div>

      {/* Revenue waterfall: Expected → −Lost → −Outstanding → =Collected, as one
          segmented bar so the gap reads as a story, not four separate tiles. */}
      <div className="flex flex-col gap-2">
        <p className="text-xs uppercase tracking-wider text-foreground/60">
          {t('notices.collection.waterfallTitle')}
        </p>
        <div className="flex h-6 w-full overflow-hidden rounded-lg" style={{ background: TRACK }}>
          {segments.map((s) => (
            <div
              key={s.key}
              className="h-full"
              style={{ width: `${w(s.value)}%`, background: s.fill }}
              title={`${t(`notices.collection.${s.key}`)}: ${formatMoney(s.value)}`}
            />
          ))}
        </div>
        <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <LegendItem swatch={COLLECTED} label={t('notices.collection.collected')} value={formatMoney(cs.collected_minor)} />
          <LegendItem swatch={OUTSTANDING} label={t('notices.collection.outstanding')} value={formatMoney(cs.outstanding_minor)} />
          <LegendItem swatch={LOST} label={t('notices.collection.lost')} value={formatMoney(cs.lost_minor)} />
          <li className="flex items-center gap-2 text-foreground/70">
            <span className="font-medium">{t('notices.collection.expected')}</span>
            <span className="tabular-nums">{formatMoney(cs.expected_minor)}</span>
          </li>
        </ul>
      </div>

      {/* Forecast range — never a single false-precision number (§11.3). */}
      <div className="flex flex-col gap-1 rounded-lg border border-border p-3">
        <p className="text-xs uppercase tracking-wider text-foreground/60">
          {t('notices.collection.forecastTitle')}
        </p>
        <p className="text-sm text-foreground/80">
          {t('notices.collection.forecastRange', {
            low: formatMoney(cs.forecast_conservative_minor),
            likely: formatMoney(cs.forecast_likely_minor),
            high: formatMoney(cs.forecast_optimistic_minor),
          })}
        </p>
      </div>

      {/* Status counts (the buckets). */}
      <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-foreground/70">
        <Count label={t('notices.collection.paid')} value={cs.paid_count} />
        <Count label={t('notices.collection.overdue')} value={cs.overdue_count} />
        <Count label={t('notices.collection.deregistered')} value={cs.deregistered_count} />
      </ul>
    </div>
  );
}

function YoyBadge({ yoy }: { yoy?: number | undefined }) {
  const { t } = useTranslation('common');
  if (yoy == null) {
    return <span className="text-sm text-foreground/50">{t('notices.collection.noYoy')}</span>;
  }
  const up = yoy >= 0;
  const sign = up ? '+' : '';
  return (
    <span
      className="inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-sm font-semibold"
      style={{
        background: up ? 'color-mix(in srgb, var(--lime) 30%, transparent)' : TRACK,
        color: 'var(--foreground)',
      }}
    >
      {up ? <TrendUp weight="bold" /> : <TrendDown weight="bold" />}
      {t('notices.collection.yoyBadge', { delta: `${sign}${yoy.toFixed(0)}%` })}
    </span>
  );
}

function LegendItem({ swatch, label, value }: { swatch: string; label: string; value: string }) {
  return (
    <li className="flex items-center gap-2">
      <span className="inline-block h-3 w-3 rounded-full" style={{ background: swatch }} aria-hidden />
      <span className="font-medium">{label}</span>
      <span className="tabular-nums text-foreground/70">{value}</span>
    </li>
  );
}

function Count({ label, value }: { label: string; value: number }) {
  return (
    <li className="flex items-center gap-1.5">
      <span className="font-semibold tabular-nums text-foreground">{value}</span>
      <span>{label}</span>
    </li>
  );
}
